import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { PaymentContextSuccessModel } from '../../domain/models/payment.types';
import type { PaymentService } from '../../application/services/PaymentService';
import {
  clearPaymentContextSession,
  readPaymentContextSession,
} from '../utils/paymentContextSession';

const formatPaymentSuccessTime = (timestamp?: number | string): string => {
  const parsedTimestamp = typeof timestamp === 'string' ? Date.parse(timestamp) : timestamp;
  const date = parsedTimestamp && !Number.isNaN(parsedTimestamp)
    ? new Date(parsedTimestamp)
    : new Date();

  const datePart = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
    .format(date)
    .replace(/\//g, '-');

  const timePart = new Intl.DateTimeFormat('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
    .format(date)
    .toLowerCase();

  return `${datePart}, ${timePart}`;
};

export function usePaymentContextSuccessResult({ service }: { service: PaymentService }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const session = readPaymentContextSession();
  const state = (location.state || {}) as {
    transactionId?: string;
    date?: number | string;
  };
  const paymentSource = params.get('paymentSource') || session?.paymentSource || 'PAY_VIA_LINK';
  const txnId = params.get('txnId') || session?.txnId || state.transactionId || '';
  const [data, setData] = useState<PaymentContextSuccessModel | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [hasRefreshError, setHasRefreshError] = useState(false);

  useEffect(() => {
    if (!txnId) return;

    let cancelled = false;

    void Promise.resolve().then(() => {
      if (cancelled) return;
      setIsLoading(true);
      setHasRefreshError(false);
    });

    service
      .getPaymentContextSuccess({ paymentSource, txnId })
      .then((successData) => {
        if (cancelled) return;
        setData(successData);
        if (successData.txnId) {
          clearPaymentContextSession();
        }
      })
      .catch(() => {
        if (!cancelled) setHasRefreshError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [paymentSource, service, txnId]);

  const successData = txnId ? data : undefined;

  return {
    isLoading: Boolean(txnId) && isLoading,
    hasRefreshError: Boolean(txnId) && hasRefreshError,
    paymentDate: formatPaymentSuccessTime(successData?.paymentSuccessTime || state.date),
  };
}
